import IconButton from '@mui/material/IconButton';
import { useEffect, useRef, useState } from 'react';
import { UndoRedoManagerCommandVersion } from '../UndoRedo/UndoRedoManagerCommandVersion';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import { useComments } from 'providers/CommentsContext';
import { useIsUndoRedoBusy } from '../hooks/useIsUndoRedoBusy';

export function EditorUndoRedoToolbar() {
  const undoRedoManagerCommandVersion = UndoRedoManagerCommandVersion.getUndoRedoManager();
  const [, forceUpdate] = useState({});
  const { commentsEnabled: commentsEnabledFromContext } = useComments();
  const undoRedoManager = undoRedoManagerCommandVersion;
  const isUndoStackEmpty = !undoRedoManagerCommandVersion.canUndo();
  const isRedoStackEmpty = !undoRedoManagerCommandVersion.canRedo();
  const isUndoRedoBusy = useIsUndoRedoBusy();
  const isBusyRef = useRef(isUndoRedoBusy);
  isBusyRef.current = isUndoRedoBusy;
  const isDisabled = isUndoRedoBusy;
  const iconButtonSX = {
    borderRadius: '4px',
    width: '38px',
    height: '40px',
    margin: '4px 2px 4px 2px',
    padding: '0',
    transition: 'none',
    disableRipple: true,

    '&:hover': {
      borderRadius: '4px',
      backgroundColor: '#ebebeb',
      transition: 'none',
    },
  };

  const secondButtonSX = {
    ...iconButtonSX,
  };

  useEffect(() => {
    const unsubscribe = undoRedoManager.subscribe(() => {
      forceUpdate({});
    });

    return unsubscribe;
  }, [undoRedoManager]);

  useEffect(() => {
    const handleKeyDown = async (event: KeyboardEvent) => {
      const isCtrlOrMetaKeyPressed = event.ctrlKey || event.metaKey;
      if (isBusyRef.current) return;
      if (!undoRedoManager.isRedoStackEmpty() && isCtrlOrMetaKeyPressed && event.shiftKey && event.key === 'z') {
        event.preventDefault();
        await undoRedoManager.runRedoCommand();
      } else if (!undoRedoManager.isUndoStackEmpty() && isCtrlOrMetaKeyPressed && !event.shiftKey && event.key === 'z') {
        event.preventDefault();
        await undoRedoManager.runUndoCommand();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [undoRedoManager]);

  return (
    <div data-testid="editor-undo-redo-wrapper" className="editor-undo-redo">
      <IconButton
        data-testid="editor-undo-button"
        size="small"
        sx={iconButtonSX}
        disabled={isUndoStackEmpty || commentsEnabledFromContext || isDisabled}
        onClick={() => undoRedoManager.runUndoCommand()}
      >
        <UndoIcon sx={{ width: '20px', height: '20px' }} />
      </IconButton>
      <IconButton
        data-testid="editor-redo-button"
        size="small"
        sx={secondButtonSX}
        disabled={isRedoStackEmpty || commentsEnabledFromContext || isDisabled}
        onClick={() => undoRedoManager.runRedoCommand()}
      >
        <RedoIcon sx={{ width: '20px', height: '20px' }} />
      </IconButton>
    </div>
  );
}
